import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Plus, Pencil, Trash2, Users, Eye, EyeOff, DollarSign,
  Percent, Hash, ChevronDown, X, UserPlus, Infinity
} from 'lucide-react';

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState('');
  const [form, setForm] = useState({
    name: '', type: 'public', monthly_price: 0, percent_fee: 0,
    tx_limit: '', extra_tx_cost: 0
  });

  const fetchPlans = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/plans`);
      setPlans(data);
    } catch (e) { toast.error('Failed to load plans'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const resetForm = () => {
    setForm({ name: '', type: 'public', monthly_price: 0, percent_fee: 0, tx_limit: '', extra_tx_cost: 0 });
    setEditingPlan(null);
    setShowForm(false);
  };

  const handleEdit = (plan) => {
    setForm({
      name: plan.name, type: plan.type, monthly_price: plan.monthly_price,
      percent_fee: plan.percent_fee, tx_limit: plan.tx_limit === null ? '' : plan.tx_limit,
      extra_tx_cost: plan.extra_tx_cost
    });
    setEditingPlan(plan);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      monthly_price: parseFloat(form.monthly_price) || 0,
      percent_fee: parseFloat(form.percent_fee) || 0,
      tx_limit: form.tx_limit === '' ? null : parseInt(form.tx_limit),
      extra_tx_cost: parseFloat(form.extra_tx_cost) || 0,
    };
    try {
      if (editingPlan) {
        await axios.put(`${API}/plans/${editingPlan.id}`, payload);
        toast.success('Plan updated');
      } else {
        await axios.post(`${API}/plans`, payload);
        toast.success('Plan created');
      }
      resetForm();
      fetchPlans();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleToggle = async (plan) => {
    try {
      await axios.patch(`${API}/plans/${plan.id}/toggle`);
      fetchPlans();
      toast.success(`Plan ${plan.is_active ? 'deactivated' : 'activated'}`);
    } catch (e) { toast.error('Failed to toggle plan'); }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try {
      await axios.delete(`${API}/plans/${plan.id}`);
      fetchPlans();
      toast.success('Plan deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete plan'); }
  };

  const openAssign = async (plan) => {
    setShowAssignModal(plan);
    try {
      const { data } = await axios.get(`${API}/admin/merchants`);
      setMerchants(data);
    } catch (e) { toast.error('Failed to load merchants'); }
  };

  const handleAssign = async () => {
    if (!selectedMerchant) return toast.error('Select a merchant');
    try {
      await axios.post(`${API}/plans/${showAssignModal.id}/assign`, { merchant_id: selectedMerchant });
      toast.success('Plan assigned');
      setShowAssignModal(null);
      setSelectedMerchant('');
      fetchPlans();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign plan'); }
  };

  return (
    <AdminLayout title="Plans">
      <div className="space-y-6" data-testid="admin-plans-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Plans & Pricing</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage subscription plans for merchants</p>
          </div>
          <Button data-testid="create-plan-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-2" /> Create Plan
          </Button>
        </div>

        {/* Plan Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => resetForm()}>
            <div className="bg-[hsl(var(--card))] border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold">{editingPlan ? 'Edit Plan' : 'Create Plan'}</h2>
                <button onClick={resetForm}><X className="w-5 h-5 text-muted-foreground" /></button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Plan Name</label>
                  <Input data-testid="plan-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required placeholder="e.g. Business" />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Type</label>
                  <select data-testid="plan-type-select" value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                    className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm">
                    <option value="public">Public (visible on pricing page)</option>
                    <option value="private">Private (admin-assigned only)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Monthly Price ($)</label>
                    <Input data-testid="plan-price-input" type="number" step="0.01" min="0" value={form.monthly_price} onChange={e => setForm({...form, monthly_price: e.target.value})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Fee per TX (%)</label>
                    <Input data-testid="plan-percent-input" type="number" step="0.01" min="0" max="100" value={form.percent_fee} onChange={e => setForm({...form, percent_fee: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">TX Limit / month</label>
                    <Input data-testid="plan-txlimit-input" type="number" min="0" value={form.tx_limit} onChange={e => setForm({...form, tx_limit: e.target.value})} placeholder="Unlimited" />
                    <p className="text-[10px] text-muted-foreground mt-0.5">Leave empty = unlimited</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Extra TX Cost ($)</label>
                    <Input data-testid="plan-extra-cost-input" type="number" step="0.01" min="0" value={form.extra_tx_cost} onChange={e => setForm({...form, extra_tx_cost: e.target.value})} />
                  </div>
                </div>
                <Button data-testid="plan-submit-btn" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Assign Modal */}
        {showAssignModal && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowAssignModal(null)}>
            <div className="bg-[hsl(var(--card))] border border-border rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold">Assign "{showAssignModal.name}" to Merchant</h2>
                <button onClick={() => setShowAssignModal(null)}><X className="w-5 h-5 text-muted-foreground" /></button>
              </div>
              <select data-testid="assign-merchant-select" value={selectedMerchant} onChange={e => setSelectedMerchant(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-foreground text-sm mb-4">
                <option value="">Select Merchant...</option>
                {merchants.map(m => (
                  <option key={m.id} value={m.id}>{m.business_name || m.full_name} ({m.email})</option>
                ))}
              </select>
              <Button data-testid="assign-plan-btn" onClick={handleAssign} className="w-full bg-indigo-600 hover:bg-indigo-700">Assign Plan</Button>
            </div>
          </div>
        )}

        {/* Plans Table */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div></div>
        ) : (
          <div className="grid gap-4">
            {plans.map(plan => (
              <div key={plan.id} data-testid={`plan-card-${plan.id}`}
                className={`bg-[hsl(var(--card))] border rounded-xl p-5 transition-all ${plan.is_active ? 'border-border' : 'border-red-500/30 opacity-60'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${plan.type === 'private' ? 'bg-purple-500/20 text-purple-400' : 'bg-indigo-500/20 text-indigo-400'}`}>
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-foreground">{plan.name}</h3>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${plan.type === 'private' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {plan.type === 'private' ? <><EyeOff className="w-3 h-3 inline mr-1" />PRIVATE</> : <><Eye className="w-3 h-3 inline mr-1" />PUBLIC</>}
                        </span>
                        {!plan.is_active && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">INACTIVE</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" />{plan.monthly_price > 0 ? `$${plan.monthly_price}/mo` : 'Free'}</span>
                        <span className="flex items-center gap-1"><Percent className="w-3.5 h-3.5" />{plan.percent_fee}% per TX</span>
                        <span className="flex items-center gap-1"><Hash className="w-3.5 h-3.5" />{plan.tx_limit === null ? <><Infinity className="w-3.5 h-3.5" /> Unlimited</> : `${plan.tx_limit} TX/mo`}</span>
                        {plan.extra_tx_cost > 0 && <span className="text-amber-400">+${plan.extra_tx_cost}/extra TX</span>}
                        <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{plan.subscriber_count || 0} subscribers</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch data-testid={`plan-toggle-${plan.id}`} checked={plan.is_active} onCheckedChange={() => handleToggle(plan)} />
                    <Button variant="ghost" size="sm" onClick={() => openAssign(plan)} data-testid={`plan-assign-${plan.id}`}><UserPlus className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(plan)} data-testid={`plan-edit-${plan.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(plan)} data-testid={`plan-delete-${plan.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
            {plans.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">No plans yet. Create your first plan.</div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPlans;
